import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner, Tabs, Tab, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine, faDollarSign, faWallet,
  faBuilding, faUsers, faSyncAlt, faExclamationTriangle,
  faInfoCircle, faChartPie, faBell,
  faFilePdf, faCalendarAlt, faDatabase, faTrash
} from '@fortawesome/free-solid-svg-icons';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import html2pdf from 'html2pdf.js';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

// --- Configuration Chart.js ---
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const API_URL = `${API_CONFIG2.apiUrl}caisse/api_financial.php`;

// --- Base de données Dexie pour le cache ---
const db = new Dexie('FinancialCacheDB');
db.version(1).stores({
  cache: 'key, data, timestamp'
});

const CACHE_DURATION_HOURS = 24;

// Fonctions de cache
const getCacheKey = (action, year) => `${action}_${year}`;

const getCachedData = async (action, year) => {
  const key = getCacheKey(action, year);
  const entry = await db.cache.get(key);
  if (!entry) return null;
  const age = (Date.now() - entry.timestamp) / (1000 * 60 * 60);
  return { data: entry.data, age, expired: age > CACHE_DURATION_HOURS };
};

const setCachedData = async (action, year, data) => {
  const key = getCacheKey(action, year);
  await db.cache.put({ key, data, timestamp: Date.now() });
};

// --- Composant principal ---
const FinancialStatements = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dataSource, setDataSource] = useState('cache');
  const [year, setYear] = useState(new Date().getFullYear());
  const [cacheAge, setCacheAge] = useState(null);
  const [cacheMessage, setCacheMessage] = useState('');

  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);
  const contentRef = useRef(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  // --- Fonction de récupération des données (avec cache) ---
  const fetchData = useCallback(async (action, forceRefresh = false) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(prev => ({ ...prev, [action]: true }));

    try {
      // 1. Vérifier le cache
      const cached = await getCachedData(action, year);
      if (cached && !forceRefresh) {
        // Données présentes et pas expirées
        if (isMounted.current) {
          setData(prev => ({ ...prev, [action]: cached.data }));
          setDataSource('cache');
          setCacheAge(Math.round(cached.age));
          setError('');
          setLoading(prev => ({ ...prev, [action]: false }));
        }
        return; // On utilise le cache
      }

      // 2. Si cache expiré ou forceRefresh, on va chercher en ligne
      const url = new URL(API_URL);
      url.searchParams.append('action', action);
      if (year) url.searchParams.append('year', year);
      if (forceRefresh) url.searchParams.append('force', '1');

      const response = await fetch(url, { signal: controller.signal });

      // Gérer 429
      if (response.status === 429) {
        // Utiliser le cache même expiré
        if (cached) {
          if (isMounted.current) {
            setData(prev => ({ ...prev, [action]: cached.data }));
            setDataSource('cache (expiré)');
            setCacheAge(Math.round(cached.age));
            setError('⚠️ Trop de requêtes. Affichage des données en cache (peuvent être obsolètes).');
            setLoading(prev => ({ ...prev, [action]: false }));
          }
          return;
        } else {
          throw new Error('Erreur 429 et aucune donnée en cache.');
        }
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const json = await response.json();
      if (json.error) throw new Error(json.error);

      // Mettre à jour le cache
      await setCachedData(action, year, json);

      if (isMounted.current) {
        setData(prev => ({ ...prev, [action]: json }));
        setDataSource('live');
        setCacheAge(0);
        setError('');
      }
    } catch (err) {
      if (err.name !== 'AbortError' && isMounted.current) {
        setError(`Erreur ${action}: ${err.message}`);
        console.error(`Erreur ${action}:`, err);
      }
    } finally {
      if (isMounted.current) {
        setLoading(prev => ({ ...prev, [action]: false }));
      }
    }
  }, [year]);

  // Chargement initial
  useEffect(() => {
    isMounted.current = true;
    fetchData('dashboard');
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchData]);

  // Changement d'onglet
  useEffect(() => {
    if (activeTab !== 'dashboard' && !data[activeTab]) {
      fetchData(activeTab);
    }
  }, [activeTab, data, fetchData]);

  // Rafraîchir tout
  const refreshAll = useCallback(() => {
    const actions = ['dashboard', 'bilan', 'compte_resultat', 'cashflow', 'caisse', 'banques', 'factures', 'salaires', 'formations', 'kpi', 'alertes'];
    actions.forEach(action => {
      fetchData(action, true);
    });
  }, [fetchData]);

  // --- Vider le cache ---
  const clearCache = async () => {
    if (window.confirm('Voulez-vous vraiment vider tout le cache ?')) {
      try {
        await db.cache.clear();
        setCacheMessage('✅ Cache vidé avec succès !');
        setDataSource('cache vidé');
        setCacheAge(null);
        // Forcer le rechargement de l'onglet actif
        fetchData(activeTab, true);
        // Effacer le message après 4 secondes
        setTimeout(() => setCacheMessage(''), 4000);
      } catch (err) {
        setError(`Erreur lors du vidage du cache : ${err.message}`);
      }
    }
  };

  // --- Utilitaires de formatage ---
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '0 USD';
    return new Intl.NumberFormat('fr-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  // --- Génération du PDF améliorée (texte noir) ---
  const generatePDF = async () => {
    if (pdfGenerating) return;
    setPdfGenerating(true);
    try {
      const element = contentRef.current;
      if (!element) return;

      // Cloner le contenu pour éviter de modifier l'affichage
      const clone = element.cloneNode(true);

      // Ajouter un en-tête et pied de page dans le clone
      const header = document.createElement('div');
      header.style.textAlign = 'center';
      header.style.padding = '15px 0';
      header.style.borderBottom = '2px solid #000';
      header.style.marginBottom = '20px';
      header.style.display = 'flex';
      header.style.justifyContent = 'space-between';
      header.style.alignItems = 'center';
      header.innerHTML = `
        <div style="font-size:20px;font-weight:700;color:#000;">Rapport financier</div>
        <div style="font-size:14px;color:#000;">${activeTab.toUpperCase()} - ${year}</div>
        <div style="font-size:12px;color:#000;">Généré le ${new Date().toLocaleString()}</div>
      `;

      const footer = document.createElement('div');
      footer.style.textAlign = 'center';
      footer.style.padding = '10px 0';
      footer.style.borderTop = '1px solid #ccc';
      footer.style.marginTop = '20px';
      footer.style.fontSize = '10px';
      footer.style.color = '#000';
      footer.textContent = 'Document confidentiel - Tous droits réservés';

      // Préparer le contenu final
      const wrapper = document.createElement('div');
      wrapper.className = 'pdf-content';
      wrapper.style.background = '#fff';
      wrapper.style.padding = '20px';
      wrapper.style.fontFamily = 'Inter, system-ui, sans-serif';
      wrapper.style.color = '#000';

      wrapper.appendChild(header);
      wrapper.appendChild(clone);
      wrapper.appendChild(footer);

      // Ajouter des styles spécifiques pour le PDF (texte noir, fond blanc)
      const style = document.createElement('style');
      style.textContent = `
        /* Forcer le texte en noir et le fond en blanc pour tout le contenu PDF */
        .pdf-content * {
          color: #000 !important;
          background-color: #fff !important;
          border-color: #ccc !important;
        }
        .pdf-content .glass-card,
        .pdf-content .kpi-card,
        .pdf-content .chart-card,
        .pdf-content .financial-table-container {
          background: #fff !important;
          border: 1px solid #ddd !important;
          box-shadow: none !important;
        }
        .pdf-content .financial-table th {
          background: #f0f0f0 !important;
          border-bottom: 2px solid #999 !important;
        }
        .pdf-content .financial-table td {
          border-bottom: 1px solid #eee !important;
        }
        .pdf-content .financial-table tr.section-title td {
          background: #e8e8e8 !important;
          font-weight: 700;
        }
        .pdf-content .financial-table tr.total td {
          background: #f5f5f5 !important;
          font-weight: 600;
          border-top: 2px solid #999 !important;
        }
        .pdf-content .financial-table tr.resultat td {
          background: #d4edda !important;
          font-weight: 700;
        }
        .pdf-content .kpi-value {
          font-size: 1.3rem;
          font-weight: 700;
          color: #000 !important;
        }
        .pdf-content .kpi-label {
          color: #333 !important;
          font-size: 0.8rem;
        }
        .pdf-content .chart-container {
          background: #fff !important;
          height: 250px;
        }
        .pdf-content .badge {
          background: #ddd !important;
          color: #000 !important;
        }
        .pdf-content .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .pdf-content .charts-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }
        .pdf-content .chart-title {
          color: #000 !important;
          font-weight: 600;
          margin-bottom: 0.8rem;
        }
        .pdf-content .financial-table {
          width: 100%;
          border-collapse: collapse;
          color: #000 !important;
        }
        .pdf-content .financial-table th,
        .pdf-content .financial-table td {
          padding: 0.5rem 0.8rem;
          text-align: left;
        }
        .pdf-content .financial-table td[style*="text-align: right"] {
          text-align: right !important;
        }
        .pdf-content .glass-card,
        .pdf-content .kpi-card {
          backdrop-filter: none !important;
          background: #fff !important;
        }
        .pdf-content canvas {
          background: #fff !important;
        }
        @media (max-width: 768px) {
          .pdf-content .charts-grid {
            grid-template-columns: 1fr;
          }
          .pdf-content .kpi-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
      `;
      wrapper.prepend(style);

      // Générer le PDF
      const opt = {
        margin: 0.5,
        filename: `rapport_financier_${activeTab}_${year}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(wrapper).save();

    } catch (err) {
      console.error('Erreur PDF:', err);
      setError('Erreur lors de la génération du PDF');
    } finally {
      setPdfGenerating(false);
    }
  };

  // --- Fonctions de rendu des onglets ---

  const renderDashboard = () => {
    const d = data.dashboard || {};
    const monthlyRevenue = d.monthlyRevenue || Array(12).fill(0);
    const monthlyExpenses = d.monthlyExpenses || Array(12).fill(0);
    const expensesByCategory = d.expensesByCategory || {};

    return (
      <>
        <div className="kpi-grid">
          <div className="kpi-card">
            <FontAwesomeIcon icon={faDollarSign} className="kpi-icon" style={{ color: '#22c55e' }} />
            <div className="kpi-value">{formatCurrency(d.totalRevenue)}</div>
            <div className="kpi-label">Chiffre d'affaires total</div>
          </div>
          <div className="kpi-card">
            <FontAwesomeIcon icon={faChartLine} className="kpi-icon" style={{ color: '#3b82f6' }} />
            <div className="kpi-value">{formatCurrency(d.netResult)}</div>
            <div className="kpi-label">Résultat net</div>
          </div>
          <div className="kpi-card">
            <FontAwesomeIcon icon={faWallet} className="kpi-icon" style={{ color: '#eab308' }} />
            <div className="kpi-value">{formatCurrency(d.cashBalance)}</div>
            <div className="kpi-label">Solde caisse</div>
          </div>
          <div className="kpi-card">
            <FontAwesomeIcon icon={faBuilding} className="kpi-icon" style={{ color: '#8b5cf6' }} />
            <div className="kpi-value">{formatCurrency(d.bankBalance)}</div>
            <div className="kpi-label">Solde banque</div>
          </div>
          <div className="kpi-card">
            <FontAwesomeIcon icon={faUsers} className="kpi-icon" style={{ color: '#ec4899' }} />
            <div className="kpi-value">{d.totalEmployees || 0}</div>
            <div className="kpi-label">Employés actifs</div>
          </div>
        </div>

        <div className="charts-grid">
          <div className="chart-card glass-card">
            <div className="chart-title"><FontAwesomeIcon icon={faChartLine} /> Évolution mensuelle</div>
            <div className="chart-container">
              <Line
                data={{
                  labels: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'],
                  datasets: [
                    { label: 'CA', data: monthlyRevenue, borderColor: '#22c55e', backgroundColor: 'rgba(34,197,94,0.1)', fill: true, tension: 0.3 },
                    { label: 'Dépenses', data: monthlyExpenses, borderColor: '#f87171', backgroundColor: 'rgba(248,113,113,0.1)', fill: true, tension: 0.3 }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { labels: { color: '#e2e8f0' } } },
                  scales: {
                    x: { grid: { display: false }, ticks: { color: '#94a3b8' } },
                    y: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#94a3b8', callback: (v) => formatCurrency(v) } }
                  }
                }}
              />
            </div>
          </div>
          {Object.keys(expensesByCategory).length > 0 && (
            <div className="chart-card glass-card">
              <div className="chart-title"><FontAwesomeIcon icon={faChartPie} /> Dépenses par catégorie</div>
              <div className="chart-container" style={{ height: '250px' }}>
                <Doughnut
                  data={{
                    labels: Object.keys(expensesByCategory),
                    datasets: [{
                      data: Object.values(expensesByCategory),
                      backgroundColor: ['#3b82f6', '#eab308', '#ef4444', '#22c55e', '#8b5cf6', '#ec4899'],
                      borderColor: '#0f172a',
                      borderWidth: 2,
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { labels: { color: '#e2e8f0', boxWidth: 12, usePointStyle: true } },
                      tooltip: { callbacks: { label: (ctx) => formatCurrency(ctx.parsed) } }
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderBilan = () => {
    const b = data.bilan || {};
    const actifs = b.actifs || {};
    const passifs = b.passifs || {};
    const capitaux = b.capitaux_propres || {};
    return (
      <div>
        <div className="financial-table-container glass-card">
          <h5>Actif</h5>
          <table className="financial-table">
            <tbody>
              <tr><td>Caisse</td><td style={{ textAlign: 'right' }}>{formatCurrency(actifs.caisse)}</td></tr>
              <tr><td>Banque</td><td style={{ textAlign: 'right' }}>{formatCurrency(actifs.banque)}</td></tr>
              <tr><td>Créances clients</td><td style={{ textAlign: 'right' }}>{formatCurrency(actifs.creances_clients)}</td></tr>
              <tr className="total"><td>Total Actif</td><td style={{ textAlign: 'right' }}>{formatCurrency(actifs.total_actif)}</td></tr>
            </tbody>
          </table>
        </div>
        <div className="financial-table-container glass-card">
          <h5>Passif & Capitaux Propres</h5>
          <table className="financial-table">
            <tbody>
              <tr><td>Dettes salariales</td><td style={{ textAlign: 'right' }}>{formatCurrency(passifs.dettes_salariales)}</td></tr>
              <tr><td>Dettes fiscales</td><td style={{ textAlign: 'right' }}>{formatCurrency(passifs.dettes_fiscales)}</td></tr>
              <tr className="total"><td>Total Passif</td><td style={{ textAlign: 'right' }}>{formatCurrency(passifs.total_passif)}</td></tr>
              <tr><td>Capitaux propres</td><td style={{ textAlign: 'right' }}>{formatCurrency(capitaux.total_capitaux)}</td></tr>
              <tr className="total"><td>Total Passif + Capitaux</td><td style={{ textAlign: 'right' }}>{formatCurrency(b.total_passif_et_capitaux)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCompteResultat = () => {
    const cr = data.compte_resultat || {};
    const produits = cr.produits || {};
    const charges = cr.charges || {};
    return (
      <div className="financial-table-container glass-card">
        <h5>Compte de résultat</h5>
        <table className="financial-table">
          <thead><tr><th>Libellé</th><th style={{ textAlign: 'right' }}>Montant</th></tr></thead>
          <tbody>
            <tr className="section-title"><td colSpan="2">PRODUITS</td></tr>
            <tr><td>Chiffre d'affaires</td><td style={{ textAlign: 'right' }}>{formatCurrency(produits.chiffre_affaires)}</td></tr>
            <tr className="total"><td>TOTAL PRODUITS</td><td style={{ textAlign: 'right' }}>{formatCurrency(produits.total_produits)}</td></tr>
            <tr className="section-title"><td colSpan="2">CHARGES</td></tr>
            <tr><td>Charges de personnel</td><td style={{ textAlign: 'right' }}>{formatCurrency(charges.charges_personnel)}</td></tr>
            <tr><td>Autres charges</td><td style={{ textAlign: 'right' }}>{formatCurrency(charges.autres_charges)}</td></tr>
            <tr className="total"><td>TOTAL CHARGES</td><td style={{ textAlign: 'right' }}>{formatCurrency(charges.total_charges)}</td></tr>
            <tr className="resultat"><td>RÉSULTAT NET</td><td style={{ textAlign: 'right' }}>{formatCurrency(cr.resultat_net)}</td></tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderCashflow = () => {
    const cf = data.cashflow || {};
    const op = cf.flux_operationnels || {};
    return (
      <div className="financial-table-container glass-card">
        <h5>Tableau des flux de trésorerie</h5>
        <table className="financial-table">
          <thead><tr><th>Flux</th><th style={{ textAlign: 'right' }}>Montant</th></tr></thead>
          <tbody>
            <tr className="section-title"><td colSpan="2">FLUX D'EXPLOITATION</td></tr>
            <tr><td>Entrées</td><td style={{ textAlign: 'right' }}>{formatCurrency(op.entrees)}</td></tr>
            <tr><td>Sorties</td><td style={{ textAlign: 'right' }}>{formatCurrency(op.sorties)}</td></tr>
            <tr className="total"><td>Net opérationnel</td><td style={{ textAlign: 'right' }}>{formatCurrency(op.net)}</td></tr>
            <tr className="resultat"><td>VARIATION DE TRÉSORERIE</td><td style={{ textAlign: 'right' }}>{formatCurrency(cf.variation_tresorerie)}</td></tr>
            <tr><td>Trésorerie de fin</td><td style={{ textAlign: 'right' }}>{formatCurrency(cf.tresorerie_fin)}</td></tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderCaisse = () => {
    const c = data.caisse || {};
    return (
      <div>
        <div className="kpi-grid mini">
          <div className="kpi-card mini"><div className="kpi-value">{formatCurrency(c.solde_actuel)}</div><div className="kpi-label">Solde actuel</div></div>
          <div className="kpi-card mini"><div className="kpi-value">{formatCurrency(c.entrees_30j)}</div><div className="kpi-label">Entrées (30j)</div></div>
          <div className="kpi-card mini"><div className="kpi-value">{formatCurrency(c.sorties_30j)}</div><div className="kpi-label">Sorties (30j)</div></div>
          <div className="kpi-card mini"><div className="kpi-value">{formatCurrency(c.flux_30j)}</div><div className="kpi-label">Flux net (30j)</div></div>
        </div>
        {c.mouvements_recents && c.mouvements_recents.length > 0 && (
          <div className="financial-table-container glass-card">
            <h5>Derniers mouvements</h5>
            <table className="financial-table">
              <thead><tr><th>Date</th><th>Type</th><th>Montant</th><th>Description</th></tr></thead>
              <tbody>
                {c.mouvements_recents.map((m, idx) => (
                  <tr key={idx}>
                    <td>{new Date(m.date).toLocaleDateString()}</td>
                    <td><Badge bg={m.type === 'Entrée' ? 'success' : 'danger'}>{m.type}</Badge></td>
                    <td>{formatCurrency(m.montant)}</td>
                    <td>{m.description || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderBanques = () => {
    const b = data.banques || {};
    const repartition = b.repartition || [];
    return (
      <div>
        <div className="financial-table-container glass-card">
          <h5>Répartition des comptes bancaires</h5>
          <table className="financial-table">
            <thead><tr><th>Banque</th><th style={{ textAlign: 'right' }}>Solde</th></tr></thead>
            <tbody>
              {repartition.map((item, idx) => (
                <tr key={idx}><td>{item.banqueNom}</td><td style={{ textAlign: 'right' }}>{formatCurrency(item.total)}</td></tr>
              ))}
              <tr className="total"><td>Total Banque</td><td style={{ textAlign: 'right' }}>{formatCurrency(b.total_banque)}</td></tr>
            </tbody>
          </table>
        </div>
        {b.derniers_transferts && b.derniers_transferts.length > 0 && (
          <div className="financial-table-container glass-card">
            <h5>Derniers transferts</h5>
            <table className="financial-table">
              <thead><tr><th>Date</th><th>Montant</th><th>Banque</th><th>Direction</th></tr></thead>
              <tbody>
                {b.derniers_transferts.map((t, idx) => (
                  <tr key={idx}>
                    <td>{new Date(t.date_transfert).toLocaleDateString()}</td>
                    <td>{formatCurrency(t.montant)}</td>
                    <td>{t.banqueNom}</td>
                    <td>{t.direction === 'caisse_to_bank' ? 'Caisse → Banque' : 'Banque → Caisse'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderFactures = () => {
    const f = data.factures || {};
    return (
      <div>
        <div className="kpi-grid mini">
          <div className="kpi-card mini"><div className="kpi-value">{formatCurrency(f.montant_facture_total)}</div><div className="kpi-label">Total facturé</div></div>
          <div className="kpi-card mini"><div className="kpi-value">{formatCurrency(f.montant_encaisse)}</div><div className="kpi-label">Encaissé</div></div>
          <div className="kpi-card mini"><div className="kpi-value">{formatCurrency(f.factures_attente?.montant)}</div><div className="kpi-label">En attente ({f.factures_attente?.nombre || 0})</div></div>
          <div className="kpi-card mini"><div className="kpi-value">{formatCurrency(f.tva_collectee)}</div><div className="kpi-label">TVA collectée</div></div>
          <div className="kpi-card mini"><div className="kpi-value">{f.clients_actifs || 0}</div><div className="kpi-label">Clients actifs</div></div>
        </div>
      </div>
    );
  };

  const renderSalaires = () => {
    const s = data.salaires || {};
    const monthly = s.evolution_mensuelle || Array(12).fill(0);
    return (
      <div>
        <div className="kpi-grid mini">
          <div className="kpi-card mini"><div className="kpi-value">{s.total_employes || 0}</div><div className="kpi-label">Employés actifs</div></div>
          <div className="kpi-card mini"><div className="kpi-value">{formatCurrency(s.masse_salariale)}</div><div className="kpi-label">Masse salariale</div></div>
          <div className="kpi-card mini"><div className="kpi-value">{formatCurrency(s.salaire_moyen)}</div><div className="kpi-label">Salaire moyen</div></div>
          <div className="kpi-card mini"><div className="kpi-value">{formatCurrency(s.total_cotisations)}</div><div className="kpi-label">Cotisations totales</div></div>
        </div>
        <div className="chart-card glass-card">
          <div className="chart-title"><FontAwesomeIcon icon={faChartLine} /> Évolution mensuelle de la masse salariale</div>
          <div className="chart-container" style={{ height: '250px' }}>
            <Bar
              data={{
                labels: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'],
                datasets: [{
                  label: 'Masse salariale',
                  data: monthly,
                  backgroundColor: 'rgba(59,130,246,0.7)',
                  borderRadius: 6,
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  x: { grid: { display: false }, ticks: { color: '#94a3b8' } },
                  y: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#94a3b8', callback: (v) => formatCurrency(v) } }
                }
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderFormations = () => {
    const f = data.formations || {};
    return (
      <div>
        <div className="kpi-grid mini">
          <div className="kpi-card mini"><div className="kpi-value">{f.nombre_formations || 0}</div><div className="kpi-label">Formations</div></div>
          <div className="kpi-card mini"><div className="kpi-value">{formatCurrency(f.prix_moyen)}</div><div className="kpi-label">Prix moyen</div></div>
          <div className="kpi-card mini"><div className="kpi-value">{formatCurrency(f.revenu_potentiel)}</div><div className="kpi-label">Revenu potentiel</div></div>
        </div>
        {f.top_categories && f.top_categories.length > 0 && (
          <div className="financial-table-container glass-card">
            <h5>Top catégories</h5>
            <table className="financial-table">
              <thead><tr><th>Catégorie</th><th>Nombre</th></tr></thead>
              <tbody>
                {f.top_categories.map((cat, idx) => (
                  <tr key={idx}><td>{cat.categorie}</td><td>{cat.count}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderKPI = () => {
    const kpiList = data.kpi || [];
    return (
      <div className="financial-table-container glass-card">
        <h5>Indicateurs clés</h5>
        <table className="financial-table">
          <thead><tr><th>Indicateur</th><th style={{ textAlign: 'right' }}>Valeur</th></tr></thead>
          <tbody>
            {kpiList.map((item, idx) => {
              let val = item.value;
              if (typeof val === 'number' && !isNaN(val)) {
                if (item.kpi.includes('%')) val = val.toFixed(2) + '%';
                else if (item.kpi.includes('Taux') || item.kpi.includes('Ratio')) val = val.toFixed(2);
                else val = formatCurrency(val);
              } else if (val === null || val === undefined) val = '—';
              return (
                <tr key={idx}><td>{item.kpi}</td><td style={{ textAlign: 'right' }}>{val}</td></tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderAlertes = () => {
    const alerts = data.alertes || [];
    if (alerts.length === 0) {
      return (
        <div className="text-center py-4 text-secondary">
          <FontAwesomeIcon icon={faBell} size="2x" className="mb-2" />
          <p>Aucune alerte à signaler.</p>
        </div>
      );
    }
    return (
      <div className="financial-table-container glass-card">
        <h5>Alertes</h5>
        <table className="financial-table">
          <thead><tr><th>Type</th><th>Message</th></tr></thead>
          <tbody>
            {alerts.map((a, idx) => (
              <tr key={idx} style={a.type === 'perte_financiere' ? { backgroundColor: 'rgba(239,68,68,0.1)' } : {}}>
                <td><Badge bg="danger">{a.type.replace(/_/g, ' ')}</Badge></td>
                <td>{a.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // --- Rendu conditionnel des onglets ---
  const isLoading = (action) => loading[action] === true;

  const renderTabContent = (action, renderFn) => {
    if (isLoading(action) && !data[action]) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-secondary mt-2">Chargement...</p>
        </div>
      );
    }
    if (error && !data[action]) {
      return (
        <div className="text-center py-4 text-danger">
          <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="mb-2" />
          <p>{error}</p>
        </div>
      );
    }
    if (!data[action]) {
      return (
        <div className="text-center py-4 text-secondary">
          <FontAwesomeIcon icon={faInfoCircle} size="2x" className="mb-2" />
          <p>Aucune donnée disponible.</p>
        </div>
      );
    }
    return (
      <div ref={contentRef}>
        {renderFn()}
      </div>
    );
  };

  // --- Rendu principal ---
  return (
    <>
      <style>{`
        .financial-wrapper {
          background: radial-gradient(ellipse at 30% 40%, #1e293b, #0b1120);
          padding: 1.5rem;
          border-radius: 28px;
          min-height: 80vh;
          margin-top: 1.5rem;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6);
          border: 1px solid rgba(255,255,255,0.03);
        }
        .header-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .header-actions h4 {
          font-weight: 700;
          color: #e2e8f0;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
          background: linear-gradient(135deg, #e2e8f0 0%, #94a3b8 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .badge-source {
          font-size: 0.7rem;
          padding: 4px 14px;
          border-radius: 40px;
          background: rgba(59,130,246,0.12);
          color: #60a5fa;
          border: 1px solid rgba(59,130,246,0.1);
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          backdrop-filter: blur(4px);
        }
        .btn-glass {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          color: #e2e8f0;
          border-radius: 40px;
          padding: 0.4rem 1.2rem;
          transition: all 0.2s ease;
          font-size: 0.9rem;
          backdrop-filter: blur(4px);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        .btn-glass:hover {
          background: rgba(255,255,255,0.10);
          border-color: rgba(255,255,255,0.15);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.3);
        }
        .btn-glass:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
        .btn-glass.pdf-btn {
          background: rgba(239,68,68,0.15);
          border-color: rgba(239,68,68,0.3);
          color: #f87171;
        }
        .btn-glass.pdf-btn:hover {
          background: rgba(239,68,68,0.25);
        }
        .btn-glass.clear-btn {
          background: rgba(234,179,8,0.12);
          border-color: rgba(234,179,8,0.2);
          color: #fbbf24;
        }
        .btn-glass.clear-btn:hover {
          background: rgba(234,179,8,0.25);
        }
        .cache-message {
          background: rgba(34,197,94,0.15);
          color: #4ade80;
          padding: 0.3rem 1rem;
          border-radius: 30px;
          border: 1px solid rgba(34,197,94,0.2);
          font-size: 0.85rem;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          backdrop-filter: blur(4px);
          animation: fadeInOut 4s forwards;
        }
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(-10px); }
          10% { opacity: 1; transform: translateY(0); }
          90% { opacity: 1; }
          100% { opacity: 0; transform: translateY(-10px); }
        }

        .glass-card {
          background: rgba(255,255,255,0.03);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 22px;
          padding: 1.2rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0,0,0,0.2);
        }
        .glass-card:hover {
          background: rgba(255,255,255,0.06);
          border-color: rgba(59,130,246,0.25);
          box-shadow: 0 8px 30px rgba(0,0,0,0.3);
        }

        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }
        .kpi-grid.mini {
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
        }
        .kpi-card {
          background: rgba(255,255,255,0.03);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 18px;
          padding: 1rem;
          text-align: center;
          transition: all 0.3s ease;
          box-shadow: 0 2px 12px rgba(0,0,0,0.1);
        }
        .kpi-card:hover {
          background: rgba(255,255,255,0.07);
          transform: translateY(-6px);
          box-shadow: 0 12px 32px rgba(0,0,0,0.3);
          border-color: rgba(59,130,246,0.2);
        }
        .kpi-card .kpi-icon { font-size: 1.6rem; display: block; margin-bottom: 0.2rem; }
        .kpi-card .kpi-value { font-size: 1.3rem; font-weight: 700; color: #fff; letter-spacing: -0.02em; }
        .kpi-card .kpi-label { font-size: 0.8rem; color: #94a3b8; margin-top: 0.2rem; text-transform: uppercase; letter-spacing: 0.04em; }

        .charts-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }
        .chart-title {
          font-weight: 600;
          color: #94a3b8;
          font-size: 0.95rem;
          margin-bottom: 0.8rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .chart-container { height: 280px; }

        .financial-table-container {
          margin-bottom: 1.5rem;
        }
        .financial-table {
          width: 100%;
          border-collapse: collapse;
          color: #e2e8f0;
          font-size: 0.9rem;
        }
        .financial-table th, .financial-table td {
          padding: 0.5rem 0.8rem;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }
        .financial-table th { color: #94a3b8; font-weight: 500; }
        .section-title td { background: rgba(59,130,246,0.1); font-weight: 600; }
        .total td { background: rgba(255,255,255,0.02); font-weight: 600; border-top: 2px solid rgba(255,255,255,0.08); }
        .resultat td { background: rgba(34,197,94,0.1); font-weight: 700; }

        .nav-tabs .nav-link {
          color: #94a3b8;
          border: none;
          padding: 0.6rem 1.2rem;
          border-radius: 30px;
          margin-right: 0.3rem;
          transition: 0.2s;
        }
        .nav-tabs .nav-link:hover { background: rgba(255,255,255,0.04); }
        .nav-tabs .nav-link.active {
          background: rgba(59,130,246,0.15);
          color: #60a5fa;
          border: 1px solid rgba(59,130,246,0.2);
        }

        .year-selector {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 40px;
          padding: 0.2rem 0.8rem;
        }
        .year-selector label {
          color: #94a3b8;
          margin: 0;
        }
        .year-selector select {
          background: transparent;
          border: none;
          color: #e2e8f0;
          padding: 0.2rem 0;
          outline: none;
        }

        @media (max-width: 768px) {
          .financial-wrapper { padding: 1rem; }
          .kpi-grid { grid-template-columns: 1fr 1fr; }
          .charts-grid { grid-template-columns: 1fr; }
          .nav-tabs .nav-link { padding: 0.4rem 0.8rem; font-size: 0.8rem; }
          .header-actions { flex-direction: column; align-items: stretch; }
          .header-actions .d-flex { flex-wrap: wrap; justify-content: center; }
          .year-selector { width: 100%; justify-content: center; }
        }
      `}</style>

      <div className="financial-wrapper">
        <div className="header-actions">
          <h4>
            <FontAwesomeIcon icon={faChartLine} className="icon" />
            États Financiers (OHADA)
          </h4>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <div className="year-selector">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-secondary" />
              <label>Année</label>
              <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
                {[2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033, 2034].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <span className="badge-source">
              <FontAwesomeIcon icon={faDatabase} />
              {dataSource === 'live' ? ' Live' : dataSource === 'cache vidé' ? ' Cache vidé' : ` Cache (${cacheAge}h)`}
            </span>
            {cacheMessage && <span className="cache-message">{cacheMessage}</span>}
            <button className="btn-glass" onClick={refreshAll} disabled={Object.values(loading).some(v => v)}>
              <FontAwesomeIcon icon={faSyncAlt} spin={Object.values(loading).some(v => v)} />
              Actualiser tout
            </button>
            <button className="btn-glass clear-btn" onClick={clearCache}>
              <FontAwesomeIcon icon={faTrash} />
              Vider le cache
            </button>
            <button className="btn-glass pdf-btn" onClick={generatePDF} disabled={pdfGenerating}>
              <FontAwesomeIcon icon={faFilePdf} />
              {pdfGenerating ? 'Génération...' : 'PDF'}
            </button>
          </div>
        </div>

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3 flex-wrap"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <Tab eventKey="dashboard" title="📊 Dashboard">{renderTabContent('dashboard', renderDashboard)}</Tab>
          <Tab eventKey="bilan" title="📋 Bilan">{renderTabContent('bilan', renderBilan)}</Tab>
          <Tab eventKey="compte_resultat" title="📈 Compte résultat">{renderTabContent('compte_resultat', renderCompteResultat)}</Tab>
          <Tab eventKey="cashflow" title="💸 Cashflow">{renderTabContent('cashflow', renderCashflow)}</Tab>
          <Tab eventKey="caisse" title="💰 Caisse">{renderTabContent('caisse', renderCaisse)}</Tab>
          <Tab eventKey="banques" title="🏦 Banques">{renderTabContent('banques', renderBanques)}</Tab>
          <Tab eventKey="factures" title="🧾 Factures">{renderTabContent('factures', renderFactures)}</Tab>
          <Tab eventKey="salaires" title="👤 Salaires">{renderTabContent('salaires', renderSalaires)}</Tab>
          <Tab eventKey="formations" title="🎓 Formations">{renderTabContent('formations', renderFormations)}</Tab>
          <Tab eventKey="kpi" title="📊 KPI">{renderTabContent('kpi', renderKPI)}</Tab>
          <Tab eventKey="alertes" title="🔔 Alertes">{renderTabContent('alertes', renderAlertes)}</Tab>
        </Tabs>
      </div>
    </>
  );
};

export default FinancialStatements;